import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, CircleCheck, Loader2, MailX } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { ResendVerificationAction } from "@/features/auth/components/resend-verification-action";
import { StatusCard } from "@/features/auth/components/status-card";
import { VerificationShell } from "@/features/auth/components/verification-shell";
import { useConfirmEmailVerification } from "@/features/auth/hooks/use-confirm-email-verification";
import { useAuth } from "@/store/use-auth";

const REDIRECT_DELAY_MS = 1800;

export function VerifyEmailPage() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const navigate = useNavigate();
	const signIn = useAuth((state) => state.signIn);
	const sessionEmail = useAuth((state) => state.session?.user.email);
	const queryClient = useQueryClient();
	const confirm = useConfirmEmailVerification(token);

	useEffect(() => {
		const session = confirm.data;
		if (!session) return;
		signIn({
			token: session.jwt,
			user: session.user,
			provider: "password",
		});
		queryClient.removeQueries({ queryKey: ["me"] });
	}, [confirm.data, signIn, queryClient]);

	useEffect(() => {
		if (!confirm.isSuccess) return;
		const id = window.setTimeout(
			() => navigate("/onboarding", { replace: true }),
			REDIRECT_DELAY_MS,
		);
		return () => window.clearTimeout(id);
	}, [confirm.isSuccess, navigate]);

	let content: ReactNode;

	if (!token || confirm.isError) {
		const expired =
			confirm.error instanceof APIError &&
			confirm.error.code === "verification-expired";
		content = (
			<StatusCard
				tone="danger"
				icon={<MailX />}
				title={expired ? "Link expirado" : "Não foi possível confirmar"}
				description={
					!token
						? "O endereço não contém um token de verificação válido."
						: expired
							? "Este link de confirmação expirou. Informe seu e-mail para receber um novo link."
							: "Este link de confirmação é inválido ou já foi utilizado."
				}
				action={
					expired ? (
						<ResendVerificationAction email={sessionEmail} />
					) : (
						<Button
							type="button"
							className="status-card__action"
							onClick={() => navigate("/onboarding")}
						>
							Voltar para o cadastro
						</Button>
					)
				}
			/>
		);
	} else if (confirm.isSuccess) {
		content = (
			<StatusCard
				tone="success"
				icon={<CircleCheck />}
				title="E-mail confirmado!"
				description="Tudo certo. Estamos te levando para o onboarding da sua obra."
				action={
					<Button
						type="button"
						className="status-card__action"
						onClick={() => navigate("/onboarding", { replace: true })}
					>
						Continuar agora <ArrowRight />
					</Button>
				}
			/>
		);
	} else {
		content = (
			<StatusCard
				tone="muted"
				icon={<Loader2 className="status-card__spinner" />}
				title="Confirmando seu e-mail"
				description="Aguarde um instante enquanto validamos seu link de confirmação."
			/>
		);
	}

	return <VerificationShell>{content}</VerificationShell>;
}
